import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Card,
  CardContent,
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography,
} from "@mui/material";
import {
  fetchInstanceDefaultsByUniqueId,
  fetchOperationsManagementSettings,
  saveInstanceDefaults,
  saveOperationsManagementSettings,
} from "../../../../services/operationsManagement";

const normalizeRepProjectViewability = (value) => value === "all" ? "all" : "scoped";
const DEFAULT_PROJECT_HEADER_SETTINGS = {
  showBookedRevenue: true,
  showBookedMargin: true,
  showInHandsDate: true,
};

const normalizeProjectHeaderSettings = (settings = {}) => {
  const parsed = settings && typeof settings === "object" && !Array.isArray(settings) ? settings : {};
  return {
    ...DEFAULT_PROJECT_HEADER_SETTINGS,
    ...parsed,
    showBookedRevenue: parsed.showBookedRevenue !== false,
    showBookedMargin: parsed.showBookedMargin !== false,
    showInHandsDate: parsed.showInHandsDate !== false,
  };
};

const PROJECT_HEADER_METRIC_OPTIONS = [
  {
    field: "showBookedRevenue",
    label: "Booked Revenue & Costs",
  },
  {
    field: "showBookedMargin",
    label: "Booked Margin",
  },
  {
    field: "showInHandsDate",
    label: "In-Hands Date",
  },
];

const SalesSettingsPanel = ({ instanceUniqueId, canManage = false }) => {
  const [repProjectViewability, setRepProjectViewability] = useState("scoped");
  const [enforceAddressBeforeSalesOrderSend, setEnforceAddressBeforeSalesOrderSend] = useState(false);
  const [requireSalesOrderCartUnlock, setRequireSalesOrderCartUnlock] = useState(false);
  const [clientApprovalFlow, setClientApprovalFlow] = useState("current");
  const [generatedHistoryVisibility, setGeneratedHistoryVisibility] = useState("everyone");
  const [instanceId, setInstanceId] = useState(null);
  const [projectHeaderSettings, setProjectHeaderSettings] = useState(DEFAULT_PROJECT_HEADER_SETTINGS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [canManageFoundation, setCanManageFoundation] = useState(false);
  const [error, setError] = useState("");

  const loadSettings = useCallback(async () => {
    if (!instanceUniqueId) return;
    setLoading(true);
    setError("");
    try {
      const [operationsResponse, defaultsResponse] = await Promise.all([
        fetchOperationsManagementSettings(instanceUniqueId),
        fetchInstanceDefaultsByUniqueId(instanceUniqueId),
      ]);
      setCanManageFoundation(operationsResponse?.permissions?.canManageFoundationalSettings === true);
      setRepProjectViewability(normalizeRepProjectViewability(
        operationsResponse?.settings?.sales?.repProjectViewability
      ));
      setEnforceAddressBeforeSalesOrderSend(
        operationsResponse?.settings?.sales?.enforceAddressBeforeSalesOrderSend === true
      );
      setRequireSalesOrderCartUnlock(
        operationsResponse?.settings?.sales?.requireSalesOrderCartUnlock === true
      );
      setGeneratedHistoryVisibility(["everyone", "users", "none"].includes(operationsResponse?.settings?.sales?.generatedHistoryVisibility)
        ? operationsResponse.settings.sales.generatedHistoryVisibility : "everyone");
      setClientApprovalFlow(
        operationsResponse?.settings?.sales?.clientApprovalFlow === "order_first"
          ? "order_first"
          : "current"
      );
      setInstanceId(defaultsResponse?.instanceId || null);
      const defaults = defaultsResponse?.defaults || {};
      setProjectHeaderSettings(normalizeProjectHeaderSettings(defaults.projectHeaderSettings));
    } catch (loadError) {
      setCanManageFoundation(false);
      setRepProjectViewability("scoped");
      setEnforceAddressBeforeSalesOrderSend(false);
      setRequireSalesOrderCartUnlock(false);
      setClientApprovalFlow("current");
      setGeneratedHistoryVisibility("everyone");
      setProjectHeaderSettings(DEFAULT_PROJECT_HEADER_SETTINGS);
      setError(loadError?.response?.data?.error || "Unable to load Sales settings.");
    } finally {
      setLoading(false);
    }
  }, [instanceUniqueId]);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const saveViewability = async (value) => {
    const normalizedValue = normalizeRepProjectViewability(value);
    setRepProjectViewability(normalizedValue);
    setSaving(true);
    setError("");
    try {
      const response = await saveOperationsManagementSettings({
        instanceUniqueId,
        sales: { repProjectViewability: normalizedValue },
      });
      setRepProjectViewability(normalizeRepProjectViewability(
        response?.settings?.sales?.repProjectViewability
      ));
    } catch (saveError) {
      setError(saveError?.response?.data?.error || "Unable to save Sales settings.");
      await loadSettings();
    } finally {
      setSaving(false);
    }
  };

  const saveAddressEnforcement = async (checked) => {
    const nextValue = checked === true;
    setEnforceAddressBeforeSalesOrderSend(nextValue);
    setSaving(true);
    setError("");
    try {
      const response = await saveOperationsManagementSettings({
        instanceUniqueId,
        sales: { enforceAddressBeforeSalesOrderSend: nextValue },
      });
      setEnforceAddressBeforeSalesOrderSend(
        response?.settings?.sales?.enforceAddressBeforeSalesOrderSend === true
      );
    } catch (saveError) {
      setError(saveError?.response?.data?.error || "Unable to save Sales settings.");
      await loadSettings();
    } finally {
      setSaving(false);
    }
  };

  const saveSalesOrderCartUnlockRequirement = async (checked) => {
    const nextValue = checked === true;
    setRequireSalesOrderCartUnlock(nextValue);
    setSaving(true);
    setError("");
    try {
      const response = await saveOperationsManagementSettings({
        instanceUniqueId,
        sales: { requireSalesOrderCartUnlock: nextValue },
      });
      setRequireSalesOrderCartUnlock(
        response?.settings?.sales?.requireSalesOrderCartUnlock === true
      );
    } catch (saveError) {
      setError(saveError?.response?.data?.error || "Unable to save Sales settings.");
      await loadSettings();
    } finally {
      setSaving(false);
    }
  };

  const saveClientApprovalFlow = async (value) => {
    const nextValue = value === "order_first" ? "order_first" : "current";
    setClientApprovalFlow(nextValue);
    setSaving(true);
    setError("");
    try {
      const response = await saveOperationsManagementSettings({
        instanceUniqueId,
        sales: { clientApprovalFlow: nextValue },
      });
      setClientApprovalFlow(
        response?.settings?.sales?.clientApprovalFlow === "order_first"
          ? "order_first"
          : "current"
      );
    } catch (saveError) {
      setError(saveError?.response?.data?.error || "Unable to save Sales settings.");
      await loadSettings();
    } finally {
      setSaving(false);
    }
  };

  const saveGeneratedHistoryVisibility = async (value) => {
    if (!["everyone", "users", "none"].includes(value)) return;
    setGeneratedHistoryVisibility(value);
    setSaving(true);
    setError("");
    try {
      const response = await saveOperationsManagementSettings({
        instanceUniqueId,
        sales: { generatedHistoryVisibility: value },
      });
      setGeneratedHistoryVisibility(response?.settings?.sales?.generatedHistoryVisibility || value);
    } catch (saveError) {
      setError(saveError?.response?.data?.error || "Unable to save Sales settings.");
      await loadSettings();
    } finally {
      setSaving(false);
    }
  };

  const saveProjectHeaderMetric = async (field, checked) => {
    if (!instanceId) return;
    const nextSettings = {
      ...projectHeaderSettings,
      [field]: checked === true,
    };
    setProjectHeaderSettings(nextSettings);
    setSaving(true);
    setError("");
    try {
      const response = await saveInstanceDefaults(instanceId, {
        projectHeaderSettings: nextSettings,
      });
      const defaults = response?.defaults || {};
      setProjectHeaderSettings(normalizeProjectHeaderSettings(defaults.projectHeaderSettings));
    } catch (saveError) {
      setError(saveError?.response?.data?.message || "Unable to save Sales settings.");
      await loadSettings();
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ display: "grid", gap: 2 }}>
      <Typography variant="body2" color="text.secondary">
        Configure project visibility for Sales representatives in this instance.
      </Typography>

      {error ? <Alert severity="error">{error}</Alert> : null}

      <Card variant="outlined">
        <CardContent>
          <FormControl disabled={!canManage || loading || saving} sx={{ display: "grid", gap: 2 }}>
            {!loading && canManage && !canManageFoundation ? (
              <Alert severity="info">Your permission level cannot change protected Sales workflow settings. Please contact a Super Administrator for this instance to request a change. You can still save regular display preferences.</Alert>
            ) : null}
            {canManageFoundation ? (<>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
              Client/Order Rep Project Viewability
            </Typography>
            <RadioGroup
              disabled={!canManageFoundation}
              value={repProjectViewability}
              onChange={(event) => saveViewability(event.target.value)}
            >
              <FormControlLabel
                value="scoped"
                control={<Radio />}
                label={
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      Assigned / Scoped Projects
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Reps only see projects allowed by the existing Client Rep / Order Rep assignment rules.
                    </Typography>
                  </Box>
                }
              />
              <FormControlLabel
                value="all"
                control={<Radio />}
                label={
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      All Projects
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Client Reps and Order Reps may view all projects in this instance.
                    </Typography>
                  </Box>
                }
              />
            </RadioGroup>

            <Divider />

            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                Sales Order Sending
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Require complete billing and shipping details before a Sales Order is emailed to the client.
              </Typography>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={enforceAddressBeforeSalesOrderSend}
                    disabled={!canManageFoundation}
                    onChange={(event) => saveAddressEnforcement(event.target.checked)}
                  />
                }
                label="Require billing & shipping details before Sales Order send"
              />
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", ml: 4 }}>
                Requires Billing Contact, Billing Address, Shipping Contact, and Shipping Address before the Send Email dialog can open.
              </Typography>
            </Box>

            <Divider />

            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                Sales Order Cart Editing
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Control whether users must explicitly unlock a Sales Order Cart before editing its snapshot.
              </Typography>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={requireSalesOrderCartUnlock}
                    disabled={!canManageFoundation}
                    onChange={(event) => saveSalesOrderCartUnlockRequirement(event.target.checked)}
                  />
                }
                label="Require unlock before editing Sales Order Cart"
              />
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", ml: 4 }}>
                Off by default. When disabled, Sales Order Carts are immediately editable. When enabled, the cart starts read-only until the user clicks Unlock.
              </Typography>
            </Box>

            <Divider />

            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                Client Approval Flow
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Choose how the client completes Sales Order approval, artwork proofing, and upfront payment.
              </Typography>
              <RadioGroup
                value={clientApprovalFlow}
                onChange={(event) => saveClientApprovalFlow(event.target.value)}
              >
                <FormControlLabel
                  value="current"
                  control={<Radio />}
                  label={
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        Current workflow
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Keep the existing proof and payment gates before final Sales Order approval.
                      </Typography>
                    </Box>
                  }
                />
                <FormControlLabel
                  value="order_first"
                  control={<Radio />}
                  label={
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        Guided order-first workflow
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Client approves the Sales Order first, then artwork proofing and deposit/payment when applicable.
                      </Typography>
                    </Box>
                  }
                />
              </RadioGroup>
            </Box>

            <Divider />

            </>) : null}
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                Generated Estimate / Sales Order History
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Who can see the history drawer in the bottom-left corner of generated estimates and sales orders?
              </Typography>
              <RadioGroup value={generatedHistoryVisibility} onChange={(event) => saveGeneratedHistoryVisibility(event.target.value)}>
                <FormControlLabel value="everyone" control={<Radio />} label="Everyone" />
                <FormControlLabel value="users" control={<Radio />} label="Only users" />
                <FormControlLabel value="none" control={<Radio />} label="No one" />
              </RadioGroup>
            </Box>

            <Divider />

            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                Project Header Metrics
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Choose which metrics appear in the project header.
              </Typography>
              <Box sx={{ display: "grid", gap: 0.5 }}>
                {PROJECT_HEADER_METRIC_OPTIONS.map((option) => (
                  <FormControlLabel
                    key={option.field}
                    control={
                      <Checkbox
                        checked={projectHeaderSettings[option.field] !== false}
                        onChange={(event) => saveProjectHeaderMetric(option.field, event.target.checked)}
                      />
                    }
                    label={option.label}
                  />
                ))}
              </Box>
            </Box>
          </FormControl>
        </CardContent>
      </Card>

      {!canManage ? (
        <Alert severity="info">You can view Sales settings, but only managers and administrators can change them.</Alert>
      ) : null}
    </Box>
  );
};

export default SalesSettingsPanel;
